import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Sparkles, CheckCircle2, XCircle, Send,
    Rss, FileText, Mic, BarChart3, Mail, ShoppingBag, Settings as SettingsIcon, Film, Trash2, Upload,
    Palette, X as XIcon, Users, Check, Shield, ShieldOff,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import { useBranding } from "../lib/branding";
import SEO from "../components/SEO";
import FileUploadField from "../components/FileUploadField";

const TABS = [
    { key: "analytics", label: "ANALYTICS", Icon: BarChart3 },
    { key: "submissions", label: "SUBMISSIONS", Icon: Mic },
    { key: "stories", label: "STORIES", Icon: FileText },
    { key: "articles", label: "ARTICLES", Icon: FileText },
    { key: "media", label: "MEDIA", Icon: Film },
    { key: "personalities", label: "PERSONALITIES", Icon: Mic },
    { key: "rss", label: "RSS INGEST", Icon: Rss },
    { key: "draft", label: "AI DRAFT", Icon: Sparkles },
    { key: "newsletter", label: "NEWSLETTER", Icon: Mail },
    { key: "orders", label: "SHOP ORDERS", Icon: ShoppingBag },
    { key: "branding", label: "BRANDING", Icon: Palette },
    { key: "editors", label: "EDITORS", Icon: Users },
    { key: "settings", label: "SETTINGS", Icon: SettingsIcon },
];

export default function AdminDashboard() {
    const { user, ready, logout } = useAuth();
    const { logoUrl, refresh: refreshBranding } = useBranding();
    const nav = useNavigate();
    const [tab, setTab] = useState("analytics");

    useEffect(() => {
        if (ready && !user) nav("/admin/login");
    }, [ready, user, nav]);

    if (!ready || !user) return null;

    return (
        <div className="bg-black text-white min-h-screen grid grid-cols-12">
            <SEO title="Editor Dashboard" noindex />
            <aside className="col-span-12 md:col-span-2 border-r border-streetz-line bg-streetz-paper p-6 flex md:flex-col gap-4 sticky top-0 md:h-screen md:overflow-y-auto">
                <Link to="/" data-testid="admin-home-link" className="flex items-center gap-2 shrink-0">
                    <img src={logoUrl} alt="MyStreetz" className="brand-logo h-8" />
                </Link>
                <nav className="flex md:flex-col gap-1 md:mt-6 flex-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`text-left overline px-3 py-3 flex items-center gap-2 border-l-2 transition-colors ${
                                tab === t.key
                                    ? "border-streetz-red bg-black text-white"
                                    : "border-transparent text-streetz-muted hover:text-white"
                            }`}
                        >
                            <t.Icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </nav>
                <button
                    onClick={() => { logout(); nav("/admin/login"); }}
                    data-testid="admin-logout-button"
                    className="overline text-streetz-muted hover:text-streetz-red inline-flex items-center gap-2"
                >
                    <LogOut className="w-4 h-4" /> LOGOUT
                </button>
            </aside>

            <main className="col-span-12 md:col-span-10 p-6 md:p-10">
                <header className="mb-8">
                    <div className="overline text-streetz-yellow">EDITOR DASHBOARD</div>
                    <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">{TABS.find(t => t.key === tab)?.label}</h1>
                    <p className="text-streetz-muted text-sm mt-1">Signed in as {user.email}</p>
                </header>

                {tab === "analytics" && <AnalyticsPanel />}
                {tab === "submissions" && <SubmissionsPanel />}
                {tab === "stories" && <StoriesPanel />}
                {tab === "articles" && <ArticlesPanel />}
                {tab === "media" && <MediaPanel />}
                {tab === "personalities" && <PersonalitiesPanel />}
                {tab === "rss" && <RssPanel />}
                {tab === "draft" && <DraftPanel />}
                {tab === "newsletter" && <NewsletterPanel />}
                {tab === "orders" && <OrdersPanel />}
                {tab === "branding" && <BrandingPanel onChange={refreshBranding} />}
                {tab === "editors" && <EditorsPanel />}
                {tab === "settings" && <SettingsPanel />}
            </main>
        </div>
    );
}

// ----------------- Panels -----------------
function StatCard({ label, value, accent = "text-streetz-yellow" }) {
    return (
        <div className="bg-streetz-paper border border-streetz-line p-5">
            <div className="overline text-streetz-muted">{label}</div>
            <div className={`font-display text-4xl mt-2 ${accent}`}>{value}</div>
        </div>
    );
}

function AnalyticsPanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
    if (!data) return <p className="text-streetz-muted">Loading…</p>;
    return (
        <div data-testid="analytics-panel" className="flex flex-col gap-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="ARTICLES PUBLISHED" value={data.articles.published} />
                <StatCard label="TOTAL VIEWS" value={data.articles.total_views} />
                <StatCard label="TOTAL SHARES" value={data.shares?.total ?? 0} accent="text-streetz-yellow" />
                <StatCard label="SUBSCRIBERS" value={data.newsletter.subscribers} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-streetz-paper border border-streetz-line p-6">
                    <h3 className="overline text-streetz-yellow">SUBMISSION FUNNEL</h3>
                    <ul className="mt-4 flex flex-col gap-2">
                        <li className="flex justify-between"><span>Total</span><span className="font-display text-2xl">{data.submissions.total}</span></li>
                        <li className="flex justify-between"><span>Pending</span><span className="font-display text-2xl text-streetz-yellow">{data.submissions.pending}</span></li>
                        <li className="flex justify-between"><span>Approved</span><span className="font-display text-2xl text-streetz-red">{data.submissions.approved}</span></li>
                        <li className="flex justify-between"><span>Rejected</span><span className="font-display text-2xl text-streetz-muted">{data.submissions.rejected}</span></li>
                    </ul>
                </div>
                <div className="bg-streetz-paper border border-streetz-line p-6">
                    <h3 className="overline text-streetz-yellow">TOP ARTICLES</h3>
                    <ol className="mt-4 flex flex-col gap-2">
                        {data.top_articles.slice(0, 5).map((a) => (
                            <li key={a.id} className="flex justify-between gap-3 text-sm">
                                <Link to={`/article/${a.slug}`} className="truncate hover:text-streetz-yellow">{a.title}</Link>
                                <span className="text-streetz-muted shrink-0">{a.views} views</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div data-testid="analytics-most-shared" className="bg-streetz-paper border border-streetz-line p-6">
                    <h3 className="overline text-streetz-yellow flex items-center justify-between">
                        <span>MOST SHARED · LAST 7 DAYS</span>
                        <span className="text-[10px] text-streetz-muted">Native + Copy-Link</span>
                    </h3>
                    {(!data.shares?.most_shared_7d || data.shares.most_shared_7d.length === 0) ? (
                        <p className="text-streetz-muted text-sm mt-4">No shares yet this week. Once visitors tap SHARE on an article, the top stories will appear here.</p>
                    ) : (
                        <ol className="mt-4 flex flex-col gap-2">
                            {data.shares.most_shared_7d.slice(0, 5).map((a) => (
                                <li key={a.id || a.slug} className="flex justify-between gap-3 text-sm">
                                    <Link to={`/article/${a.slug}`} className="truncate hover:text-streetz-yellow">{a.title || a.slug}</Link>
                                    <span className="text-streetz-red font-display shrink-0">{a.shares} shares</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
                <div className="bg-streetz-paper border border-streetz-line p-6">
                    <h3 className="overline text-streetz-yellow">SHOP</h3>
                    <ul className="mt-4 flex flex-col gap-2">
                        <li className="flex justify-between"><span>Paid orders</span><span className="font-display text-2xl text-streetz-red">{data.shop.paid_orders}</span></li>
                        <li className="flex justify-between"><span>Revenue (USD)</span><span className="font-display text-2xl text-streetz-yellow">${data.shop.revenue_usd}</span></li>
                    </ul>
                </div>
            </div>
            <div className="bg-streetz-paper border border-streetz-line p-6">
                <h3 className="overline text-streetz-yellow">BY CATEGORY</h3>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.by_category.map((c) => (
                        <div key={c.category} className="border border-streetz-line p-3">
                            <div className="overline text-streetz-muted">{c.category}</div>
                            <div className="font-display text-2xl">{c.count}</div>
                            <div className="text-xs text-streetz-muted">{c.views} views</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SubmissionsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try { setItems((await api.get("/admin/submissions")).data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const decide = async (id, decision) => {
        try {
            await api.patch(`/admin/submissions/${id}/decision`, null, { params: { decision } });
            toast.success(`Submission ${decision}`);
            load();
        } catch { toast.error("Failed to update"); }
    };
    const remove = async (s) => {
        if (!window.confirm(`Delete submission from "${s.artist_name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/submissions/${s.id}`);
            toast.success("Submission deleted");
            load();
        } catch { toast.error("Failed to delete"); }
    };
    if (loading) return <p className="text-streetz-muted">Loading submissions…</p>;
    if (!items.length) return <p className="text-streetz-muted">No submissions yet.</p>;
    return (
        <div data-testid="admin-submissions-list" className="flex flex-col gap-4">
            {items.map((s) => (
                <article key={s.id} className="bg-streetz-paper border border-streetz-line p-6">
                    <header className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                            <h3 className="font-display uppercase text-2xl">{s.artist_name}</h3>
                            <p className="text-streetz-muted text-sm">{s.genre} · {s.location} · {s.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`overline px-2 py-1 ${s.status === "pending" ? "bg-streetz-yellow text-black" : s.status === "approved" ? "bg-streetz-red text-white" : "bg-streetz-line text-white"}`}>
                                {s.status.toUpperCase()}
                            </span>
                            {s.ai_review?.score != null && (
                                <span className="overline border border-streetz-yellow text-streetz-yellow px-2 py-1">
                                    AI {s.ai_review.score}/10 · {s.ai_review.recommendation?.toUpperCase()}
                                </span>
                            )}
                        </div>
                    </header>
                    <p className="text-sm text-white/80 mt-3">{s.bio}</p>
                    {s.ai_review?.profile && <p className="text-sm text-white/70 mt-3 border-l-2 border-streetz-red pl-4">{s.ai_review.profile}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-4">
                        <a href={s.song_url} target="_blank" rel="noopener noreferrer" className="overline text-streetz-yellow hover:underline" data-testid={`submission-${s.id}-song-link`}>LISTEN ↗</a>
                        {s.status === "approved" && (
                            <Link to={`/artist/${s.id}`} className="overline border border-streetz-yellow text-streetz-yellow px-3 py-2 hover:bg-streetz-yellow hover:text-black">VIEW PUBLIC PROFILE ↗</Link>
                        )}
                        {s.status === "pending" && (
                            <>
                                <button onClick={() => decide(s.id, "approved")} data-testid={`submission-${s.id}-approve`} className="overline bg-streetz-red text-white px-3 py-2 inline-flex items-center gap-2 hover:bg-white hover:text-black">
                                    <CheckCircle2 className="w-4 h-4" /> APPROVE
                                </button>
                                <button onClick={() => decide(s.id, "rejected")} data-testid={`submission-${s.id}-reject`} className="overline border border-streetz-line text-white px-3 py-2 inline-flex items-center gap-2 hover:border-streetz-red">
                                    <XCircle className="w-4 h-4" /> REJECT
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => remove(s)}
                            data-testid={`submission-${s.id}-delete`}
                            title="Delete submission permanently"
                            className="overline border border-streetz-line text-streetz-muted px-3 py-2 inline-flex items-center gap-2 hover:border-streetz-red hover:text-streetz-red ml-auto"
                        >
                            <Trash2 className="w-4 h-4" /> DELETE
                        </button>
                    </div>
                </article>
            ))}
        </div>
    );
}


function StoriesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try { setItems((await api.get("/admin/stories")).data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/stories/${id}`, { status });
            toast.success(status === "approved" ? "Story approved" : "Story rejected");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    };
    const remove = async (s) => {
        if (!window.confirm(`Delete "${s.title}" by ${s.name}?`)) return;
        try {
            await api.delete(`/admin/stories/${s.id}`);
            toast.success("Deleted");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };
    if (loading) return <p className="text-streetz-muted">Loading…</p>;
    if (!items.length) return <p className="text-streetz-muted" data-testid="admin-stories-empty">No reader stories yet. Once readers submit at /story they&apos;ll show up here.</p>;
    const pending = items.filter((x) => x.status === "pending").length;
    return (
        <div data-testid="admin-stories-panel" className="flex flex-col gap-6">
            <div className="overline text-streetz-yellow">{pending} PENDING REVIEW · {items.length} TOTAL</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-stories-list">
                {items.map((s) => (
                    <div key={s.id} className="bg-streetz-paper border border-streetz-line p-5 flex flex-col gap-3" data-testid={`story-${s.id}-card`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-display uppercase text-xl line-clamp-2">{s.title}</div>
                                <div className="overline text-[10px] text-streetz-muted mt-1">
                                    {s.name} · {s.email}{s.location ? ` · ${s.location}` : ""}
                                </div>
                            </div>
                            <span className={`overline px-2 py-1 shrink-0 ${s.status === "approved" ? "bg-streetz-yellow text-black" : s.status === "rejected" ? "bg-streetz-line text-streetz-muted" : "bg-streetz-red text-white"}`}>
                                {s.status.toUpperCase()}
                            </span>
                        </div>
                        {s.image_url && (
                            <img src={s.image_url} alt={s.title} className="w-full h-40 object-cover border border-streetz-line" />
                        )}
                        <p className="text-sm text-streetz-muted whitespace-pre-wrap line-clamp-6">{s.body}</p>
                        {s.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {s.tags.map((t) => (
                                    <span key={t} className="overline text-[10px] text-streetz-yellow border border-streetz-yellow/40 px-2 py-1">{t}</span>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {s.status !== "approved" && (
                                <button onClick={() => setStatus(s.id, "approved")} data-testid={`story-${s.id}-approve`} className="overline bg-streetz-yellow text-black px-3 py-1 hover:bg-white">
                                    APPROVE
                                </button>
                            )}
                            {s.status !== "rejected" && (
                                <button onClick={() => setStatus(s.id, "rejected")} data-testid={`story-${s.id}-reject`} className="overline border border-streetz-line px-3 py-1 hover:border-streetz-red hover:text-streetz-red">
                                    REJECT
                                </button>
                            )}
                            <button onClick={() => remove(s)} data-testid={`story-${s.id}-delete`} className="overline border border-streetz-line px-3 py-1 hover:border-streetz-red hover:text-streetz-red inline-flex items-center gap-1">
                                <Trash2 className="w-3 h-3" /> DELETE
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function ArticlesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const load = async () => { setLoading(true); try { setItems((await api.get("/admin/articles")).data); } finally { setLoading(false); } };
    useEffect(() => { load(); }, []);
    const update = async (id, body) => { await api.patch(`/admin/articles/${id}`, body); toast.success("Article updated"); load(); };
    const wpPublish = async (id) => {
        toast.info("Publishing to WordPress…");
        try {
            const r = await api.post(`/admin/articles/${id}/wp-publish`);
            toast.success(`Pushed to WP. ID ${r.data.id}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "WP publish failed");
        }
    };
    const newArticle = async () => {
        setCreating(true);
        try {
            const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
            const r = await api.post("/admin/articles", {
                title: `Untitled draft — ${stamp}`,
                category: "Music",
                excerpt: "",
                body: "",
                use_ai: false,
            });
            toast.success("Draft created. Fill it in below.");
            await load();
            setEditing(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create article");
        } finally {
            setCreating(false);
        }
    };
    if (loading) return <p className="text-streetz-muted">Loading articles…</p>;
    return (
        <div data-testid="admin-articles-list" className="overflow-x-auto">
            <div className="flex items-center justify-between mb-4">
                <p className="text-streetz-muted text-sm">{items.length} article{items.length === 1 ? "" : "s"} total</p>
                <button
                    onClick={newArticle}
                    disabled={creating}
                    data-testid="article-new-button"
                    className="overline bg-streetz-yellow text-black px-4 py-2 hover:bg-white disabled:opacity-60 inline-flex items-center gap-2"
                >
                    <FileText className="w-4 h-4" />
                    {creating ? "CREATING…" : "+ NEW ARTICLE"}
                </button>
            </div>
            {!items.length ? (
                <p className="text-streetz-muted">No articles yet. Click <strong>+ NEW ARTICLE</strong> above or use the <strong>AI DRAFT</strong> tab.</p>
            ) : (
            <table className="w-full text-left border-collapse min-w-[1080px]">
                <thead>
                    <tr className="overline text-streetz-muted border-b border-streetz-line">
                        <th className="py-3 pr-4">Title</th>
                        <th className="py-3 pr-4">Category</th>
                        <th className="py-3 pr-4">Status</th>
                        <th className="py-3 pr-4">Source</th>
                        <th className="py-3 pr-4">OG Share</th>
                        <th className="py-3 pr-4">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((a) => (
                        <tr key={a.id} className="border-b border-streetz-line hover:bg-streetz-paper">
                            <td className="py-3 pr-4">
                                <button
                                    onClick={() => setEditing(a)}
                                    data-testid={`article-${a.id}-edit`}
                                    className="text-left hover:text-streetz-yellow underline decoration-dotted underline-offset-4"
                                >
                                    {a.title}
                                </button>
                                {a.hero && <span className="ml-2 overline text-streetz-yellow">★ TOP</span>}
                                {a.ai_generated && <span className="ml-2 overline text-streetz-yellow">AI</span>}
                                {a.wp_post_url && <span className="ml-2 overline text-streetz-red">WP</span>}
                            </td>
                            <td className="py-3 pr-4 text-streetz-muted">{a.category}</td>
                            <td className="py-3 pr-4">
                                <span className={`overline px-2 py-1 ${a.status === "published" ? "bg-streetz-red text-white" : a.status === "approved" ? "bg-streetz-yellow text-black" : "bg-streetz-line text-white"}`}>
                                    {a.status.toUpperCase()}
                                </span>
                            </td>
                            <td className="py-3 pr-4 text-streetz-muted overline">{a.source}</td>
                            <td className="py-3 pr-4">
                                <select
                                    value={a.og_layout || "auto"}
                                    onChange={(e) => update(a.id, { og_layout: e.target.value })}
                                    data-testid={`article-${a.id}-og-layout`}
                                    className="bg-black border border-streetz-line text-white overline text-[10px] px-2 py-1"
                                    title="Social share card layout"
                                >
                                    <option value="auto">AUTO</option>
                                    <option value="cover">COVER</option>
                                    <option value="brand">BRAND</option>
                                </select>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/og/${a.slug}.png`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`article-${a.id}-og-preview`}
                                    className="overline text-streetz-yellow ml-2 underline hover:text-white"
                                >
                                    PREVIEW
                                </a>
                            </td>
                            <td className="py-3 pr-4 flex flex-wrap gap-2">
                                {a.status !== "published" && (
                                    <button onClick={() => update(a.id, { status: "published" })} data-testid={`article-${a.id}-publish`} className="overline bg-streetz-red text-white px-2 py-1 hover:bg-white hover:text-black">PUBLISH</button>
                                )}
                                <button
                                    onClick={() => update(a.id, { hero: !a.hero })}
                                    data-testid={`article-${a.id}-toggle-hero`}
                                    title={a.hero ? "Remove from the homepage Top Story carousel" : "Promote to Top Story on the homepage hero"}
                                    className={`overline px-2 py-1 inline-flex items-center gap-1 ${a.hero ? "bg-streetz-yellow text-black hover:bg-white" : "border border-streetz-line hover:border-streetz-yellow"}`}
                                >
                                    {a.hero ? "★ TOP STORY" : "MAKE TOP STORY"}
                                </button>
                                <button onClick={() => update(a.id, { trending: !a.trending })} data-testid={`article-${a.id}-toggle-trending`} className="overline border border-streetz-line px-2 py-1 hover:border-streetz-yellow">{a.trending ? "UNTREND" : "TREND"}</button>
                                <button onClick={() => wpPublish(a.id)} data-testid={`article-${a.id}-wp-publish`} className="overline border border-streetz-line px-2 py-1 hover:border-streetz-red inline-flex items-center gap-1"><Upload className="w-3 h-3" /> WP</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            )}
            {editing && (
                <ArticleEditModal
                    article={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function ArticleEditModal({ article, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: article.title || "",
        category: article.category || "Music",
        excerpt: article.excerpt || "",
        body: article.body || "",
        image_url: article.image_url || "",
        og_layout: article.og_layout || "auto",
        tags: (article.tags || []).join(", "),
        seo_title: article.seo_title || "",
        meta_description: article.meta_description || "",
        status: article.status || "draft",
        hero: !!article.hero,
        trending: !!article.trending,
    });
    const [saving, setSaving] = useState(false);
    const [previewKey, setPreviewKey] = useState(Date.now());
    // Debounced refresh of the OG preview as the editor types
    useEffect(() => {
        const t = setTimeout(() => setPreviewKey(Date.now()), 500);
        return () => clearTimeout(t);
    }, [form.title, form.category, form.image_url, form.og_layout]);
    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                category: form.category,
                excerpt: form.excerpt,
                body: form.body,
                image_url: form.image_url,
                og_layout: form.og_layout,
                status: form.status,
                hero: form.hero,
                trending: form.trending,
                seo_title: form.seo_title,
                meta_description: form.meta_description,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            // Persist proposed image_url first so the OG renderer picks up the latest cover
            await api.patch(`/admin/articles/${article.id}`, payload);
            toast.success("Article saved");
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    const ogUrl = `${API}/og/${article.slug}.png?v=${previewKey}`;
    return (
        <div
            data-testid="article-edit-modal"
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-streetz-paper border border-streetz-line max-w-6xl w-full my-8"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-streetz-line">
                    <div>
                        <div className="overline text-streetz-yellow">EDITING ARTICLE</div>
                        <h2 className="font-display uppercase text-2xl mt-1 line-clamp-1">{form.title || "Untitled"}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="article-edit-close"
                        aria-label="Close"
                        className="h-9 w-9 grid place-items-center text-streetz-muted hover:text-streetz-red"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-6">
                    {/* Left: form */}
                    <div className="lg:col-span-3 flex flex-col gap-4">
                        <Field label="Headline" testid="edit-title">
                            <input value={form.title} onChange={handle("title")} className="bg-black border border-streetz-line px-3 py-2 w-full" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Category" testid="edit-category">
                                <input value={form.category} onChange={handle("category")} className="bg-black border border-streetz-line px-3 py-2 w-full" />
                            </Field>
                            <Field label="Status" testid="edit-status">
                                <select value={form.status} onChange={handle("status")} className="bg-black border border-streetz-line px-3 py-2 w-full">
                                    <option value="draft">DRAFT</option>
                                    <option value="approved">APPROVED</option>
                                    <option value="published">PUBLISHED</option>
                                </select>
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-testid="edit-flags">
                            <label className="flex items-center gap-2 bg-black border border-streetz-line px-3 py-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.hero}
                                    onChange={(e) => setForm({ ...form, hero: e.target.checked })}
                                    data-testid="edit-hero"
                                    className="accent-streetz-yellow"
                                />
                                <span className="text-sm">
                                    <strong className="text-streetz-yellow">★ TOP STORY</strong>
                                    <span className="block text-[11px] text-streetz-muted">Show in the homepage hero carousel</span>
                                </span>
                            </label>
                            <label className="flex items-center gap-2 bg-black border border-streetz-line px-3 py-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.trending}
                                    onChange={(e) => setForm({ ...form, trending: e.target.checked })}
                                    data-testid="edit-trending"
                                    className="accent-streetz-red"
                                />
                                <span className="text-sm">
                                    <strong className="text-streetz-red">TRENDING</strong>
                                    <span className="block text-[11px] text-streetz-muted">Surface in the &ldquo;Trending Now&rdquo; rail + breaking ticker</span>
                                </span>
                            </label>
                        </div>
                        <Field label="Excerpt (30-45 words)" testid="edit-excerpt">
                            <textarea value={form.excerpt} onChange={handle("excerpt")} rows={3} className="bg-black border border-streetz-line px-3 py-2 w-full font-sans" />
                        </Field>
                        <Field label="Body" testid="edit-body">
                            <textarea value={form.body} onChange={handle("body")} rows={10} className="bg-black border border-streetz-line px-3 py-2 w-full font-sans whitespace-pre-wrap" />
                        </Field>
                        <Field label="Cover image URL" testid="edit-image-url">
                            <input value={form.image_url} onChange={handle("image_url")} placeholder="https://… (paste a URL or upload)" className="bg-black border border-streetz-line px-3 py-2 w-full" />
                            <FileUploadField
                                endpoint="/uploads/article-image"
                                accept="image/jpeg,image/png,image/webp"
                                label="Replace cover (optional)"
                                testId="edit-cover-upload"
                                auth
                                onUploaded={(url) => setForm({ ...form, image_url: url })}
                            />
                        </Field>
                        <Field label="Tags (comma-separated)" testid="edit-tags">
                            <input value={form.tags} onChange={handle("tags")} className="bg-black border border-streetz-line px-3 py-2 w-full" />
                        </Field>
                        <Field label="SEO title (optional, defaults to headline)" testid="edit-seo-title">
                            <input value={form.seo_title} onChange={handle("seo_title")} className="bg-black border border-streetz-line px-3 py-2 w-full" />
                        </Field>
                        <Field label="Meta description (optional, defaults to excerpt)" testid="edit-meta">
                            <textarea value={form.meta_description} onChange={handle("meta_description")} rows={2} className="bg-black border border-streetz-line px-3 py-2 w-full font-sans" />
                        </Field>
                    </div>

                    {/* Right: live OG preview */}
                    <div className="lg:col-span-2 flex flex-col gap-3">
                        <div className="overline text-streetz-yellow flex items-center justify-between">
                            <span>SOCIAL SHARE PREVIEW</span>
                            <span className="text-[10px] text-streetz-muted">1200 × 630</span>
                        </div>
                        <div className="flex gap-2">
                            {["auto", "cover", "brand"].map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setForm({ ...form, og_layout: opt })}
                                    data-testid={`edit-og-${opt}`}
                                    className={`flex-1 overline text-[10px] py-2 border ${
                                        form.og_layout === opt
                                            ? "bg-streetz-yellow text-black border-streetz-yellow"
                                            : "border-streetz-line text-white hover:border-streetz-yellow"
                                    }`}
                                >
                                    {opt.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <div className="bg-black border border-streetz-line aspect-[1200/630] grid place-items-center overflow-hidden">
                            <img
                                key={previewKey}
                                src={ogUrl}
                                alt="OG card preview"
                                data-testid="edit-og-preview-img"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        </div>
                        <p className="text-[11px] text-streetz-muted">
                            Preview refreshes ~500ms after you stop typing. It reflects the saved DB state — click <strong>SAVE</strong> below to update the live cover/title used by social media crawlers.
                        </p>
                        <a
                            href={ogUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="edit-og-open-tab"
                            className="overline text-streetz-yellow underline self-start"
                        >
                            OPEN FULL SIZE
                        </a>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-streetz-line">
                    <button
                        onClick={onClose}
                        data-testid="article-edit-cancel"
                        className="overline border border-streetz-line px-4 py-2 hover:border-streetz-red"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="article-edit-save"
                        className="overline bg-streetz-red text-white px-5 py-2 hover:bg-white hover:text-black disabled:opacity-60"
                    >
                        {saving ? "SAVING…" : "SAVE"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, testid, children }) {
    return (
        <label className="flex flex-col gap-1" data-testid={testid}>
            <span className="overline text-[10px] text-streetz-muted">{label}</span>
            {children}
        </label>
    );
}

function MediaPanel() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ type: "podcast", title: "", description: "", image_url: "", media_url: "", host: "", duration: "" });
    const load = async () => setItems((await api.get("/admin/media")).data);
    useEffect(() => { load(); }, []);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        await api.post("/admin/media", form);
        toast.success("Media added");
        setForm({ ...form, title: "", description: "", image_url: "", media_url: "", host: "", duration: "" });
        load();
    };
    const del = async (id) => { await api.delete(`/admin/media/${id}`); toast.success("Removed"); load(); };
    return (
        <div className="flex flex-col gap-8">
            <form onSubmit={submit} data-testid="admin-media-form" className="bg-streetz-paper border border-streetz-line p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" data-testid="media-type-select">
                    {["podcast", "playlist", "interview", "video", "event"].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
                <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black border border-streetz-line px-3 py-2 md:col-span-2" data-testid="media-title-input" />
                <input placeholder="Host / Subject" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" />
                <input placeholder="Duration (e.g. 32 min)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" />
                <input placeholder="External media URL" value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" />
                <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-black border border-streetz-line px-3 py-2 md:col-span-3" />
                <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black border border-streetz-line px-3 py-2 md:col-span-3 resize-none" />
                <button className="bg-streetz-red text-white overline py-2 md:col-span-3 hover:bg-white hover:text-black" data-testid="media-add-button">ADD MEDIA ITEM</button>
            </form>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-media-list">
                {items.map((m) => (
                    <li key={m.id} className="bg-streetz-paper border border-streetz-line p-4 flex gap-3">
                        {m.image_url && <img src={m.image_url} alt="" className="w-20 h-20 object-cover" />}
                        <div className="flex-1">
                            <div className="overline text-streetz-yellow">{m.type.toUpperCase()}</div>
                            <div className="font-display uppercase text-lg">{m.title}</div>
                            <div className="text-xs text-streetz-muted">{m.host} · {m.duration}</div>
                        </div>
                        <button onClick={() => del(m.id)} aria-label="delete" className="h-8 w-8 grid place-items-center border border-streetz-line hover:border-streetz-red text-streetz-muted hover:text-streetz-red"><Trash2 className="w-4 h-4" /></button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function PersonalitiesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", role: "Artist", image_url: "", order: 0 });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setItems((await api.get("/admin/personalities")).data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Name is required");
        setCreating(true);
        try {
            await api.post("/admin/personalities", {
                name: form.name.trim(),
                role: form.role.trim() || "Artist",
                image_url: form.image_url,
                order: Number(form.order) || 0,
            });
            toast.success("Personality added");
            setForm({ name: "", role: "Artist", image_url: "", order: 0 });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const update = async (id, body) => {
        try {
            await api.patch(`/admin/personalities/${id}`, body);
            toast.success("Saved");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This removes them from the homepage rail.`)) return;
        try {
            await api.delete(`/admin/personalities/${p.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="admin-personalities-panel" className="flex flex-col gap-8">
            <form onSubmit={create} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-4 max-w-3xl" data-testid="personality-create-form">
                <div className="flex items-center justify-between">
                    <h3 className="overline text-streetz-yellow">ADD A NEW PERSONALITY</h3>
                    <span className="overline text-[10px] text-streetz-muted">SHOWS ON THE HOMEPAGE RAIL</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Name</span>
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="personality-name"
                            placeholder="Asake"
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Role / Description</span>
                        <input
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            data-testid="personality-role"
                            placeholder="Afrobeats Star"
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Image URL (or upload below)</span>
                        <input
                            value={form.image_url}
                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                            data-testid="personality-image-url"
                            placeholder="https://…"
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Order (lower = leftmost)</span>
                        <input
                            type="number"
                            value={form.order}
                            onChange={(e) => setForm({ ...form, order: e.target.value })}
                            data-testid="personality-order"
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                </div>
                <FileUploadField
                    endpoint="/uploads/personality-image"
                    accept="image/jpeg,image/png,image/webp"
                    label="Upload photo (optional — auto-fills the URL field)"
                    testId="personality-photo-upload"
                    auth
                    onUploaded={(url) => setForm({ ...form, image_url: url })}
                />
                <button
                    disabled={creating}
                    data-testid="personality-create-submit"
                    className="self-start overline bg-streetz-red text-white px-5 py-2 hover:bg-white hover:text-black disabled:opacity-60"
                >
                    {creating ? "ADDING…" : "ADD PERSONALITY"}
                </button>
            </form>

            <div>
                <h3 className="overline text-streetz-yellow mb-3">CURRENT PERSONALITIES ({items.length})</h3>
                {loading ? (
                    <p className="text-streetz-muted">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="text-streetz-muted">No personalities yet. Add one above.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[760px]" data-testid="personalities-table">
                            <thead>
                                <tr className="overline text-streetz-muted border-b border-streetz-line">
                                    <th className="py-3 pr-4">Photo</th>
                                    <th className="py-3 pr-4">Name</th>
                                    <th className="py-3 pr-4">Role / Image URL</th>
                                    <th className="py-3 pr-4">Order</th>
                                    <th className="py-3 pr-4">Visible</th>
                                    <th className="py-3 pr-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <PersonalityRow key={p.id} p={p} onUpdate={update} onDelete={remove} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function PersonalityRow({ p, onUpdate, onDelete }) {
    const [draft, setDraft] = useState({
        name: p.name,
        role: p.role,
        image_url: p.image_url,
        order: p.order,
        visible: p.visible !== false,
    });
    const dirty =
        draft.name !== p.name ||
        draft.role !== p.role ||
        draft.image_url !== p.image_url ||
        Number(draft.order) !== Number(p.order) ||
        draft.visible !== (p.visible !== false);
    const save = () => onUpdate(p.id, {
        name: draft.name,
        role: draft.role,
        image_url: draft.image_url,
        order: Number(draft.order) || 0,
        visible: draft.visible,
    });
    const toggleVisible = () => {
        const next = !draft.visible;
        setDraft({ ...draft, visible: next });
        // Persist immediately — visibility toggle is a one-click action, not a draft.
        onUpdate(p.id, { visible: next });
    };
    return (
        <tr className={`border-b border-streetz-line hover:bg-streetz-paper align-top ${draft.visible ? "" : "opacity-50"}`}>
            <td className="py-3 pr-4">
                {draft.image_url ? (
                    <img
                        src={draft.image_url}
                        alt={draft.name}
                        className="w-14 h-14 object-cover rounded-full border border-streetz-line"
                    />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-streetz-line grid place-items-center text-streetz-muted text-xs">—</div>
                )}
            </td>
            <td className="py-3 pr-4">
                <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    data-testid={`personality-${p.id}-name`}
                    className="bg-black border border-streetz-line px-2 py-1 w-full max-w-[200px]"
                />
            </td>
            <td className="py-3 pr-4">
                <input
                    value={draft.role}
                    onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                    data-testid={`personality-${p.id}-role`}
                    className="bg-black border border-streetz-line px-2 py-1 w-full max-w-[200px]"
                />
                <input
                    value={draft.image_url}
                    onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
                    data-testid={`personality-${p.id}-image`}
                    placeholder="image URL"
                    className="bg-black border border-streetz-line px-2 py-1 w-full max-w-[260px] mt-2 text-[11px]"
                />
            </td>
            <td className="py-3 pr-4">
                <input
                    type="number"
                    value={draft.order}
                    onChange={(e) => setDraft({ ...draft, order: e.target.value })}
                    data-testid={`personality-${p.id}-order`}
                    className="bg-black border border-streetz-line px-2 py-1 w-20"
                />
            </td>
            <td className="py-3 pr-4">
                <button
                    type="button"
                    onClick={toggleVisible}
                    data-testid={`personality-${p.id}-toggle-visible`}
                    title={draft.visible ? "Hide from the homepage rail" : "Show on the homepage rail"}
                    className={`overline px-3 py-1 inline-flex items-center gap-2 ${
                        draft.visible
                            ? "bg-streetz-yellow text-black hover:bg-white"
                            : "border border-streetz-line text-streetz-muted hover:border-streetz-yellow hover:text-streetz-yellow"
                    }`}
                >
                    {draft.visible ? "✓ VISIBLE" : "HIDDEN"}
                </button>
            </td>
            <td className="py-3 pr-4 flex flex-col gap-1">
                <button
                    onClick={save}
                    disabled={!dirty}
                    data-testid={`personality-${p.id}-save`}
                    className={`overline px-2 py-1 ${dirty ? "bg-streetz-red text-white hover:bg-white hover:text-black" : "bg-streetz-line text-streetz-muted cursor-not-allowed"}`}
                >
                    {dirty ? "SAVE" : "SAVED"}
                </button>
                <button
                    onClick={() => onDelete(p)}
                    data-testid={`personality-${p.id}-delete`}
                    className="overline border border-streetz-line px-2 py-1 hover:border-streetz-red hover:text-streetz-red inline-flex items-center gap-1"
                >
                    <Trash2 className="w-3 h-3" /> DELETE
                </button>
            </td>
        </tr>
    );
}


function RssPanel() {
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState(""); const [url, setUrl] = useState("");
    const load = async () => { setLoading(true); try { setFeeds((await api.get("/admin/rss")).data); } finally { setLoading(false); } };
    useEffect(() => { load(); }, []);
    const add = async (e) => { e.preventDefault(); if (!name || !url) return; await api.post("/admin/rss", { name, url }); toast.success("Added"); setName(""); setUrl(""); load(); };
    const ingest = async (id) => { toast.info("Pulling + AI rewriting…"); try { const r = await api.post(`/admin/rss/${id}/ingest`); toast.success(`Ingested ${r.data.ingested} drafts`); } catch { toast.error("Ingest failed"); } };
    return (
        <div className="flex flex-col gap-6">
            <p className="overline text-streetz-muted">Auto-ingestion runs in the background every 2 hours (toggle in Settings).</p>
            <form onSubmit={add} data-testid="admin-rss-add-form" className="bg-streetz-paper border border-streetz-line p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Feed name" className="bg-black border border-streetz-line px-3 py-2" data-testid="rss-feed-name" />
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="bg-black border border-streetz-line px-3 py-2" data-testid="rss-feed-url" />
                <button className="bg-streetz-red text-white overline px-4 py-2 hover:bg-white hover:text-black" data-testid="rss-feed-add-button">ADD FEED</button>
            </form>
            {loading ? <p className="text-streetz-muted">Loading…</p> : (
                <ul className="flex flex-col gap-3" data-testid="admin-rss-list">
                    {feeds.map((f) => (
                        <li key={f.id} className="bg-streetz-paper border border-streetz-line p-4 flex flex-wrap items-center gap-3 justify-between">
                            <div>
                                <div className="font-display uppercase text-lg">{f.name}</div>
                                <div className="text-xs text-streetz-muted break-all">{f.url}</div>
                                <div className="text-xs text-streetz-muted">Last fetched: {f.last_fetched || "never"}</div>
                            </div>
                            <button onClick={() => ingest(f.id)} data-testid={`rss-${f.id}-ingest`} className="overline bg-streetz-yellow text-black px-3 py-2 inline-flex items-center gap-2 hover:bg-white">
                                <RefreshCw className="w-4 h-4" /> INGEST NOW
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function DraftPanel() {
    const [raw, setRaw] = useState(""); const [result, setResult] = useState(null); const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const run = async (e) => { e.preventDefault(); if (!raw.trim()) return; setLoading(true); try { setResult((await api.post("/admin/ai/draft", { raw_text: raw, category: "Music" })).data); } catch { toast.error("AI draft failed"); } finally { setLoading(false); } };
    const save = async () => {
        if (!result) return;
        try {
            await api.post("/admin/articles", {
                title: result.title, category: "Music",
                excerpt: result.excerpt, body: result.body,
                image_url: imageUrl, trending: true, use_ai: false,
            });
            toast.success("Draft saved");
            setRaw(""); setResult(null); setImageUrl("");
        }
        catch { toast.error("Could not save draft"); }
    };
    return (
        <div className="flex flex-col gap-6 max-w-3xl">
            <form onSubmit={run} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-3" data-testid="ai-draft-form">
                <label className="overline">Raw input (paste news / press release / notes)</label>
                <textarea rows={8} value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="ai-draft-input" className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none resize-none" />
                <button disabled={loading} data-testid="ai-draft-run-button" className="bg-streetz-red text-white overline py-3 inline-flex items-center justify-center gap-2 hover:bg-white hover:text-black disabled:opacity-60">
                    {loading ? "GENERATING…" : (<>RUN AI DRAFT <Sparkles className="w-4 h-4" /></>)}
                </button>
            </form>
            {result && (
                <div className="bg-streetz-paper border border-streetz-yellow p-6 flex flex-col gap-4" data-testid="ai-draft-result">
                    <div className="overline text-streetz-yellow">AI DRAFT</div>
                    <h3 className="font-display uppercase text-3xl">{result.title}</h3>
                    <p className="text-white/85">{result.excerpt}</p>
                    <p className="text-white/70 whitespace-pre-line">{result.body}</p>
                    <FileUploadField
                        endpoint="/uploads/article-image"
                        accept="image/jpeg,image/png,image/webp"
                        label="Featured image (optional)"
                        testId="article-image-upload"
                        auth
                        onUploaded={setImageUrl}
                    />
                    {imageUrl && (
                        <img src={imageUrl} alt="" className="w-full max-h-48 object-cover border border-streetz-line" data-testid="article-image-preview" />
                    )}
                    <button onClick={save} data-testid="ai-draft-save-button" className="bg-streetz-yellow text-black overline py-3 inline-flex items-center justify-center gap-2 hover:bg-white">
                        <Send className="w-4 h-4" /> SAVE AS TRENDING DRAFT
                    </button>
                </div>
            )}
        </div>
    );
}

function NewsletterPanel() {
    const [subs, setSubs] = useState({ count: 0, subscribers: [] });
    const [digests, setDigests] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = async () => {
        const [s, d] = await Promise.all([
            api.get("/admin/newsletter/subscribers"),
            api.get("/admin/newsletter/digests"),
        ]);
        setSubs(s.data); setDigests(d.data);
    };
    useEffect(() => { load(); }, []);
    const generate = async () => {
        setLoading(true);
        try { await api.post("/admin/newsletter/digest/generate"); toast.success("Digest generated"); load(); }
        catch { toast.error("Digest generation failed"); }
        finally { setLoading(false); }
    };
    const send = async (id) => {
        try {
            const r = await api.post(`/admin/newsletter/digest/${id}/send`);
            if (r.data.provider_used) toast.success(`Sent to ${r.data.sent_count} subscribers via Resend`);
            else toast.info(`Marked as sent. ${r.data.recipients_total} subscribers — add Resend key in Settings to actually send.`);
            load();
        } catch { toast.error("Send failed"); }
    };
    return (
        <div data-testid="admin-newsletter-panel" className="flex flex-col gap-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard label="SUBSCRIBERS" value={subs.count} />
                <StatCard label="DIGESTS GENERATED" value={digests.length} />
                <StatCard label="DIGESTS SENT" value={digests.filter(d => d.status === "sent").length} accent="text-streetz-red" />
            </div>
            <button onClick={generate} disabled={loading} data-testid="generate-digest-button" className="self-start bg-streetz-red text-white overline px-5 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-black disabled:opacity-60">
                <Sparkles className="w-4 h-4" /> {loading ? "GENERATING…" : "GENERATE WEEKLY DIGEST"}
            </button>
            <ul className="flex flex-col gap-3" data-testid="digests-list">
                {digests.map((d) => (
                    <li key={d.id} className="bg-streetz-paper border border-streetz-line p-5">
                        <div className="flex justify-between items-start gap-2">
                            <div>
                                <div className="overline text-streetz-yellow">{d.status.toUpperCase()} · {d.created_at?.slice(0, 10)}</div>
                                <h3 className="font-display uppercase text-2xl">{d.title}</h3>
                                <p className="text-streetz-muted text-sm mt-1">{d.intro}</p>
                            </div>
                            {d.status === "draft" ? (
                                <button onClick={() => send(d.id)} data-testid={`digest-${d.id}-send`} className="overline bg-streetz-yellow text-black px-3 py-2 hover:bg-white inline-flex items-center gap-2">
                                    <Send className="w-4 h-4" /> SEND
                                </button>
                            ) : (
                                <span className="overline text-streetz-red">SENT · {d.sent_count}</span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function OrdersPanel() {
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
    return (
        <div className="overflow-x-auto" data-testid="orders-panel">
            <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                    <tr className="overline text-streetz-muted border-b border-streetz-line">
                        <th className="py-3 pr-4">Created</th>
                        <th className="py-3 pr-4">Email</th>
                        <th className="py-3 pr-4">Items</th>
                        <th className="py-3 pr-4">Total</th>
                        <th className="py-3 pr-4">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 && (
                        <tr><td colSpan={5} className="py-6 text-streetz-muted">No orders yet.</td></tr>
                    )}
                    {orders.map((o) => (
                        <tr key={o.id} className="border-b border-streetz-line">
                            <td className="py-3 pr-4 text-sm">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                            <td className="py-3 pr-4 text-sm">{o.email || "—"}</td>
                            <td className="py-3 pr-4 text-sm">{(o.items || []).map(i => `${i.name} ×${i.qty}`).join(", ")}</td>
                            <td className="py-3 pr-4 font-display text-streetz-yellow">${(o.amount || 0).toFixed(2)}</td>
                            <td className="py-3 pr-4">
                                <span className={`overline px-2 py-1 ${o.payment_status === "paid" ? "bg-streetz-red text-white" : "bg-streetz-line text-white"}`}>
                                    {(o.payment_status || "—").toUpperCase()}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function EditorsPanel() {
    const { user: me } = useAuth();
    const isAdmin = me?.role === "admin";
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", email: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setUsers((await api.get("/admin/editors")).data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const invite = async (e) => {
        e.preventDefault();
        if (!form.email) return toast.error("Email is required");
        setSubmitting(true);
        try {
            const r = await api.post("/admin/editors/invite", form);
            toast.success(`Invite sent to ${r.data.user.email}`);
            setForm({ name: "", email: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        } finally { setSubmitting(false); }
    };

    const revoke = async (u) => {
        const isPending = u.role === "pending";
        const msg = isPending
            ? `Reject the access request from ${u.email}? This removes their pending request.`
            : `Revoke editor access for ${u.email}? This will delete their account.`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/admin/editors/${u.id}`);
            toast.success(isPending ? `Rejected ${u.email}` : `Revoked ${u.email}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    const approve = async (u) => {
        try {
            await api.post(`/admin/editors/${u.id}/approve`);
            toast.success(`${u.email} approved — they can now sign in as an editor`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Approve failed");
        }
    };

    const setRole = async (u, role) => {
        const msg = role === "admin"
            ? `Make ${u.email} an ADMIN? They will get full control, including managing the team and settings.`
            : `Demote ${u.email} to EDITOR? They will lose admin controls.`;
        if (!window.confirm(msg)) return;
        try {
            await api.post(`/admin/editors/${u.id}/role`, { role });
            toast.success(`${u.email} is now ${role === "admin" ? "an admin" : "an editor"}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Role change failed");
        }
    };

    const resendInvite = async (u) => {
        if (!window.confirm(`Re-send the welcome email (with a fresh temporary password) to ${u.email}?`)) return;
        try {
            await api.post(`/admin/editors/${u.id}/resend-invite`);
            toast.success(`Invite re-sent to ${u.email}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        }
    };

    return (
        <div data-testid="admin-editors-panel" className="flex flex-col gap-8 max-w-4xl">
            <form onSubmit={invite} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-4" data-testid="editor-invite-form">
                <div className="flex items-center justify-between">
                    <h3 className="overline text-streetz-yellow">INVITE A NEW EDITOR</h3>
                    <span className="overline text-[10px] text-streetz-muted">SENDS A WELCOME EMAIL VIA RESEND</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Editor name</span>
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="invite-name"
                            placeholder="Jane Doe"
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Editor email</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="invite-email"
                            placeholder="jane@yourdomain.com"
                            required
                            className="bg-black border border-streetz-line px-3 py-2"
                        />
                    </label>
                </div>
                <button
                    disabled={submitting}
                    data-testid="invite-submit"
                    className="self-start overline bg-streetz-red text-white px-5 py-2 hover:bg-white hover:text-black disabled:opacity-60"
                >
                    {submitting ? "SENDING INVITE…" : "INVITE EDITOR"}
                </button>
                <p className="text-[11px] text-streetz-muted">
                    The editor receives an email with their temporary password, the dashboard URL, and the Operational Manual PDF link.
                    They&apos;re prompted to change the password from <em>Settings → Account Security</em> on first login.
                </p>
                <p className="text-[11px] text-streetz-muted border-t border-streetz-line pt-3">
                    <strong className="text-white">No email needed?</strong> Collaborators can also click <em>&quot;Continue with Google&quot;</em> on the
                    login page. Their first sign-in shows up here as <span className="text-streetz-yellow">PENDING</span> — just hit APPROVE to let them in.
                </p>
            </form>

            <div>
                <h3 className="overline text-streetz-yellow mb-3">CURRENT TEAM ({users.length})</h3>
                {loading ? (
                    <p className="text-streetz-muted">Loading…</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[640px]" data-testid="editors-table">
                            <thead>
                                <tr className="overline text-streetz-muted border-b border-streetz-line">
                                    <th className="py-3 pr-4">Email</th>
                                    <th className="py-3 pr-4">Name</th>
                                    <th className="py-3 pr-4">Role</th>
                                    <th className="py-3 pr-4">Joined</th>
                                    <th className="py-3 pr-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className="border-b border-streetz-line hover:bg-streetz-paper">
                                        <td className="py-3 pr-4">{u.email}</td>
                                        <td className="py-3 pr-4 text-streetz-muted">{u.name || "—"}</td>
                                        <td className="py-3 pr-4">
                                            <span className={`overline px-2 py-1 ${u.role === "admin" ? "bg-streetz-red text-white" : u.role === "pending" ? "bg-streetz-yellow text-black" : "bg-streetz-line text-white"}`}>
                                                {u.role.toUpperCase()}
                                            </span>
                                            {u.must_change_password && (
                                                <span className="ml-2 overline text-[10px] text-streetz-yellow">PW PENDING</span>
                                            )}
                                            {u.auth_provider === "google" && (
                                                <span className="ml-2 overline text-[10px] text-streetz-muted">GOOGLE</span>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4 text-streetz-muted overline">{u.created_at?.slice(0, 10)}</td>
                                        <td className="py-3 pr-4">
                                            {u.role === "admin" ? (
                                                isAdmin && u.id !== me?.id ? (
                                                    <button
                                                        onClick={() => setRole(u, "editor")}
                                                        data-testid={`demote-${u.id}`}
                                                        className="overline border border-streetz-line px-2 py-1 hover:border-streetz-yellow hover:text-streetz-yellow inline-flex items-center gap-1"
                                                        title="Demote this admin to editor"
                                                    >
                                                        <ShieldOff className="w-3 h-3" /> MAKE EDITOR
                                                    </button>
                                                ) : (
                                                    <span className="overline text-[10px] text-streetz-muted">{u.id === me?.id ? "YOU" : "PROTECTED"}</span>
                                                )
                                            ) : u.role === "pending" ? (
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => approve(u)}
                                                        data-testid={`approve-${u.id}`}
                                                        className="overline border border-streetz-line px-2 py-1 hover:border-streetz-yellow hover:text-streetz-yellow inline-flex items-center gap-1"
                                                        title="Approve this Google access request as an editor"
                                                    >
                                                        <Check className="w-3 h-3" /> APPROVE
                                                    </button>
                                                    <button
                                                        onClick={() => revoke(u)}
                                                        data-testid={`reject-${u.id}`}
                                                        className="overline border border-streetz-line px-2 py-1 hover:border-streetz-red hover:text-streetz-red inline-flex items-center gap-1"
                                                    >
                                                        <Trash2 className="w-3 h-3" /> REJECT
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => setRole(u, "admin")}
                                                            data-testid={`promote-${u.id}`}
                                                            className="overline border border-streetz-line px-2 py-1 hover:border-streetz-red hover:text-streetz-red inline-flex items-center gap-1"
                                                            title="Give this editor full admin control"
                                                        >
                                                            <Shield className="w-3 h-3" /> MAKE ADMIN
                                                        </button>
                                                    )}
                                                    {u.must_change_password && (
                                                        <button
                                                            onClick={() => resendInvite(u)}
                                                            data-testid={`resend-invite-${u.id}`}
                                                            className="overline border border-streetz-line px-2 py-1 hover:border-streetz-yellow hover:text-streetz-yellow inline-flex items-center gap-1"
                                                            title="Send a fresh welcome email with a new temporary password"
                                                        >
                                                            <Send className="w-3 h-3" /> RESEND INVITE
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => revoke(u)}
                                                        data-testid={`revoke-${u.id}`}
                                                        className="overline border border-streetz-line px-2 py-1 hover:border-streetz-red hover:text-streetz-red inline-flex items-center gap-1"
                                                    >
                                                        <Trash2 className="w-3 h-3" /> REVOKE
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function SettingsPanel() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
    if (!s) return <p className="text-streetz-muted">Loading…</p>;
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {};
            for (const k of ["ga_tracking_id", "wp_url", "wp_username", "wp_app_password", "resend_api_key", "resend_from_email", "radio_stream_url", "radio_now_playing", "radio_meta_url", "radio_push_token"]) {
                if (s[k] !== undefined) payload[k] = s[k];
            }
            payload.rss_cron_enabled = !!s.rss_cron_enabled;
            payload.radio_on_air = !!s.radio_on_air;
            await api.put("/admin/settings", payload);
            toast.success("Settings saved");
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    const verifyWP = async () => {
        setVerifying(true);
        try {
            const r = await api.post("/admin/wp/verify");
            if (r.data.ok) toast.success(`WP OK: ${r.data.message}`);
            else toast.error(`WP failed (${r.data.status_code}): ${r.data.message}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
        } finally { setVerifying(false); }
    };
    const Chip = ({ on, label }) => (
        <span className={`overline text-[10px] px-2 py-[2px] ${on ? "bg-streetz-yellow text-black" : "bg-streetz-line text-streetz-muted"}`}>
            {on ? "CONFIGURED" : label}
        </span>
    );
    return (
        <div>
        <form onSubmit={save} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-4 max-w-2xl" data-testid="admin-settings-form">
            <label className="flex flex-col gap-1">
                <span className="overline">Google Analytics tracking ID (e.g. G-XXXXXXX)</span>
                <input value={s.ga_tracking_id || ""} onChange={(e) => setS({ ...s, ga_tracking_id: e.target.value })} data-testid="settings-ga-input" className="bg-black border border-streetz-line px-3 py-2" />
            </label>
            <hr className="border-streetz-line" />
            <div className="flex items-center justify-between">
                <h3 className="overline text-streetz-yellow">WORDPRESS PUBLISH-OUT</h3>
                <Chip on={!!s.wp_app_password_set} label="NOT CONFIGURED" />
            </div>
            <label className="flex flex-col gap-1">
                <span className="overline">WP site URL (https://yoursite.com)</span>
                <input value={s.wp_url || ""} onChange={(e) => setS({ ...s, wp_url: e.target.value })} data-testid="settings-wp-url" className="bg-black border border-streetz-line px-3 py-2" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">WP admin username</span>
                <input value={s.wp_username || ""} onChange={(e) => setS({ ...s, wp_username: e.target.value })} data-testid="settings-wp-user" className="bg-black border border-streetz-line px-3 py-2" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">WP Application Password (24 chars in 6 groups, e.g. xxxx xxxx xxxx xxxx xxxx xxxx) {s.wp_app_password_set && "(saved, paste new to replace)"}</span>
                <input type="password" value={s.wp_app_password || ""} onChange={(e) => setS({ ...s, wp_app_password: e.target.value })} data-testid="settings-wp-pass" className="bg-black border border-streetz-line px-3 py-2" placeholder={s.wp_app_password_set ? "•••••••• (saved)" : "Users → Profile → Application Passwords"} />
            </label>
            <button type="button" onClick={verifyWP} disabled={verifying} data-testid="verify-wp-button" className="self-start overline border border-streetz-yellow text-streetz-yellow px-3 py-2 hover:bg-streetz-yellow hover:text-black disabled:opacity-60">
                {verifying ? "TESTING…" : "TEST WP CONNECTION"}
            </button>
            <hr className="border-streetz-line" />
            <div className="flex items-center justify-between">
                <h3 className="overline text-streetz-yellow">NEWSLETTER PROVIDER</h3>
                <Chip on={!!s.resend_api_key_set} label="NOT CONFIGURED" />
            </div>
            <label className="flex flex-col gap-1">
                <span className="overline">Resend API key {s.resend_api_key_set && "(saved)"}</span>
                <input type="password" value={s.resend_api_key || ""} onChange={(e) => setS({ ...s, resend_api_key: e.target.value })} data-testid="settings-resend-key" className="bg-black border border-streetz-line px-3 py-2" placeholder={s.resend_api_key_set ? "•••••••• (saved)" : "re_xxx..."} />
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">Sender email (Resend &lsquo;from&rsquo;)</span>
                <input
                    value={s.resend_from_email || ""}
                    onChange={(e) => setS({ ...s, resend_from_email: e.target.value })}
                    data-testid="settings-resend-from"
                    className="bg-black border border-streetz-line px-3 py-2"
                    placeholder="MyStreetzMag <editor@yourdomain.com>"
                />
                <span className="text-[11px] text-streetz-muted">
                    Default is <code>onboarding@resend.dev</code> (sandbox — only sends to your Resend signup email).
                    Verify a domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-streetz-yellow underline">resend.com/domains</a> then paste a verified address here.
                </span>
            </label>
            <hr className="border-streetz-line" />
            <h4 className="overline text-streetz-yellow">RADIO / LIVE STREAM</h4>
            <label className="flex flex-col gap-1">
                <span className="overline">Radio stream URL</span>
                <input
                    value={s.radio_stream_url || ""}
                    onChange={(e) => setS({ ...s, radio_stream_url: e.target.value })}
                    data-testid="settings-radio-stream-url"
                    placeholder="https://stream.example.com/listen or Mixcloud / SoundCloud / Spotify URL"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
                <span className="text-[11px] text-streetz-muted">
                    Direct Icecast/Shoutcast .mp3 stream, Mixcloud or SoundCloud show URL, or Spotify playlist as a stand-in. The mini-player picks the right embed automatically.
                </span>
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">Now playing label</span>
                <input
                    value={s.radio_now_playing || ""}
                    onChange={(e) => setS({ ...s, radio_now_playing: e.target.value })}
                    data-testid="settings-radio-now-playing"
                    placeholder="DJ Kaywise · Afrobeats Street Mix"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
                <span className="text-[11px] text-streetz-muted">
                    Free-text fallback. If you wire a live now-playing feed below, the live ARTIST · SONG · ALBUM will override this whenever the feed responds.
                </span>
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">Now playing feed URL (optional)</span>
                <input
                    value={s.radio_meta_url || ""}
                    onChange={(e) => setS({ ...s, radio_meta_url: e.target.value })}
                    data-testid="settings-radio-meta-url"
                    placeholder="https://stream.example.com/status-json.xsl or https://feed.example.com/nowplaying.xml"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
                <span className="text-[11px] text-streetz-muted">
                    JSON or RSS endpoint exposing the current track. Auto-detects Icecast/Shoutcast/Centova/RadioKing-style JSON and standard RSS feeds (the latest item title is split on " - ").
                    The widget polls every 30s and the backend caches each response for 20s.
                </span>
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline inline-flex items-center gap-2">
                    Radio push token <Chip on={s.radio_push_token_set} label={s.radio_push_token_set ? "CONFIGURED" : "OFF"} />
                </span>
                <input
                    type="text"
                    value={s.radio_push_token || ""}
                    onChange={(e) => setS({ ...s, radio_push_token: e.target.value })}
                    data-testid="settings-radio-push-token"
                    placeholder={s.radio_push_token_set ? "•••••••• (leave blank to keep current token)" : "e.g. a 32-char random string"}
                    className="bg-black border border-streetz-line px-3 py-2 font-mono text-sm"
                />
                <span className="text-[11px] text-streetz-muted">
                    Enables the <code>POST /api/radio/push</code> webhook for RadioDJ / Centova / Liquidsoap.
                    Send the token in an <code>X-Radio-Token</code> header on every push. <strong>Take this overrides the polling feed for 5 minutes after each push.</strong>
                </span>
                {s.radio_push_token_set && (
                    <div className="bg-black border border-streetz-line p-3 text-[11px] text-streetz-muted mt-1" data-testid="settings-radio-push-snippet">
                        <div className="overline text-streetz-yellow mb-1">RADIODJ WEBHOOK CONFIG</div>
                        <div className="text-[11px] mb-2 text-streetz-yellow">RadioDJ v2 FREE / "Now Playing Info Exporter" — use the URL-token format (the Password field is not sent on the FREE build):</div>
                        <div>URL: <code className="text-white break-all">{process.env.REACT_APP_BACKEND_URL}/api/radio/push/&lt;your-token&gt;</code></div>
                        <div>Method: <code className="text-white">POST</code></div>
                        <div>Content-Type: <code className="text-white">application/x-www-form-urlencoded</code></div>
                        <div>Custom Data: <code className="text-white">{`title=$title$&artist=$artist$&album=$album$`}</code></div>
                        <div className="text-[10px] mt-1 text-streetz-muted">Other clients can still send the token via X-Radio-Token header, Basic Auth password, or ?token= query.</div>
                        <hr className="border-streetz-line my-2" />
                        <div className="overline text-streetz-yellow mb-1">LAST RECEIVED PUSH</div>
                        {s.radio_last_push_at ? (
                            <div data-testid="settings-radio-last-push">
                                <div><strong className="text-white">{s.radio_last_push_artist || "—"}</strong> · {s.radio_last_push_song || "—"}</div>
                                <div className="text-[10px]">
                                    {s.radio_last_push_age_seconds < 60
                                        ? `${s.radio_last_push_age_seconds}s ago`
                                        : s.radio_last_push_age_seconds < 3600
                                            ? `${Math.floor(s.radio_last_push_age_seconds / 60)} min ago`
                                            : `${Math.floor(s.radio_last_push_age_seconds / 3600)}h ago`
                                    }
                                    {" · "}{new Date(s.radio_last_push_at).toLocaleString()}
                                </div>
                                {s.radio_last_push_age_seconds > 300 && (
                                    <div className="text-streetz-red text-[10px] mt-1">⚠ More than 5 min old — widget has dropped back to fallback. Check RadioDJ's HTTP plugin.</div>
                                )}
                            </div>
                        ) : (
                            <div data-testid="settings-radio-no-pushes" className="text-streetz-red">
                                ⚠ No pushes received yet. Verify RadioDJ HTTP plugin is enabled and pointing to the URL above.
                            </div>
                        )}
                    </div>
                )}
            </label>
            <label className="flex items-start gap-3">
                <input
                    type="checkbox"
                    checked={!!s.radio_on_air}
                    onChange={(e) => setS({ ...s, radio_on_air: e.target.checked })}
                    data-testid="settings-radio-on-air-toggle"
                    className="mt-1"
                />
                <span className="flex flex-col gap-1">
                    <span className="overline">Currently on air (manual override)</span>
                    <span className="text-[11px] text-streetz-muted">
                        Forces the red <strong>ON AIR</strong> badge + animated EQ on the homepage.
                        <strong className="text-streetz-yellow"> Not required if RadioDJ is pushing</strong> —
                        any push within the last 5 min auto-flips the badge on.
                    </span>
                </span>
            </label>
            <hr className="border-streetz-line" />
            <label className="flex items-center gap-3">
                <input type="checkbox" checked={!!s.rss_cron_enabled} onChange={(e) => setS({ ...s, rss_cron_enabled: e.target.checked })} data-testid="settings-cron-toggle" />
                <span className="overline">Auto RSS ingestion enabled</span>
            </label>
            <button disabled={saving} data-testid="settings-save-button" className="bg-streetz-red text-white overline py-3 hover:bg-white hover:text-black disabled:opacity-60">
                {saving ? "SAVING…" : "SAVE SETTINGS"}
            </button>
            </form>
            <ChangePasswordCard />
        </div>
    );
}

function ChangePasswordCard() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!current || !next) return toast.error("Fill both password fields");
        if (next.length < 8) return toast.error("New password must be at least 8 characters");
        if (next !== confirm) return toast.error("New passwords don't match");
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated. Keep it safe.");
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update password");
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <form onSubmit={submit} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-4 max-w-2xl mt-6" data-testid="admin-change-password-form">
            <div className="flex items-center justify-between">
                <h3 className="overline text-streetz-yellow">ACCOUNT SECURITY</h3>
                <span className="overline text-[10px] text-streetz-muted">CHANGE LOGIN PASSWORD</span>
            </div>
            <label className="flex flex-col gap-1">
                <span className="overline">Current password</span>
                <input
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    data-testid="change-pw-current"
                    autoComplete="current-password"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">New password (min 8 chars)</span>
                <input
                    type="password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    data-testid="change-pw-new"
                    autoComplete="new-password"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
            </label>
            <label className="flex flex-col gap-1">
                <span className="overline">Confirm new password</span>
                <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="change-pw-confirm"
                    autoComplete="new-password"
                    className="bg-black border border-streetz-line px-3 py-2"
                />
            </label>
            <button disabled={submitting} data-testid="change-pw-submit" className="bg-streetz-yellow text-black overline py-3 hover:bg-white disabled:opacity-60">
                {submitting ? "UPDATING…" : "UPDATE PASSWORD"}
            </button>
        </form>
    );
}


function BrandingPanel({ onChange }) {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
    if (!s) return <p className="text-streetz-muted">Loading…</p>;
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/settings", {
                logo_url: s.logo_url || "",
                favicon_url: s.favicon_url || "",
                logo_height_px: Number(s.logo_height_px) || 0,
                font_hero_px: Number(s.font_hero_px) || 0,
                font_section_px: Number(s.font_section_px) || 0,
                font_body_px: Number(s.font_body_px) || 0,
                font_nav_px: Number(s.font_nav_px) || 0,
            });
            toast.success("Branding saved");
            onChange?.();
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    const reset = async () => {
        if (!window.confirm("Reset all branding to defaults?")) return;
        await api.put("/admin/settings", {
            logo_url: "", favicon_url: "", logo_height_px: 0,
            font_hero_px: 0, font_section_px: 0, font_body_px: 0, font_nav_px: 0,
        });
        const r = await api.get("/admin/settings");
        setS(r.data);
        toast.success("Branding reset");
        onChange?.();
    };
    const num = (key) => (
        <input
            type="number"
            min={0}
            max={300}
            value={s[key] || ""}
            placeholder="default"
            onChange={(e) => setS({ ...s, [key]: e.target.value === "" ? 0 : Number(e.target.value) })}
            data-testid={`branding-${key.replace(/_/g, "-")}`}
            className="bg-black border border-streetz-line px-3 py-2 w-32"
        />
    );
    return (
        <form onSubmit={save} className="bg-streetz-paper border border-streetz-line p-6 flex flex-col gap-6 max-w-3xl" data-testid="admin-branding-form">
            <section className="flex flex-col gap-4">
                <h3 className="overline text-streetz-yellow">LOGO & FAVICON</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                        <FileUploadField
                            endpoint="/uploads/branding-logo"
                            accept="image/jpeg,image/png,image/webp"
                            label="Upload new logo (PNG/JPEG/WebP)"
                            testId="branding-logo-upload"
                            onUploaded={(url) => setS({ ...s, logo_url: url })}
                        />
                        <label className="flex flex-col gap-1">
                            <span className="overline text-streetz-muted">Logo URL (or paste an external URL)</span>
                            <input value={s.logo_url || ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" data-testid="branding-logo-url" />
                        </label>
                        {s.logo_url && (
                            <div className="bg-black border border-streetz-line p-3 flex items-center justify-center min-h-[80px]">
                                <img src={s.logo_url} alt="Logo preview" className="h-10 w-auto" data-testid="branding-logo-preview" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <FileUploadField
                            endpoint="/uploads/branding-favicon"
                            accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/jpeg,image/webp"
                            label="Upload favicon (.ico / .png recommended 32×32)"
                            testId="branding-favicon-upload"
                            onUploaded={(url) => setS({ ...s, favicon_url: url })}
                        />
                        <label className="flex flex-col gap-1">
                            <span className="overline text-streetz-muted">Favicon URL</span>
                            <input value={s.favicon_url || ""} onChange={(e) => setS({ ...s, favicon_url: e.target.value })} className="bg-black border border-streetz-line px-3 py-2" data-testid="branding-favicon-url" />
                        </label>
                        {s.favicon_url && (
                            <div className="bg-black border border-streetz-line p-3 flex items-center justify-center min-h-[80px]">
                                <img src={s.favicon_url} alt="Favicon preview" className="h-8 w-8" data-testid="branding-favicon-preview" />
                            </div>
                        )}
                    </div>
                </div>
                <label className="flex items-center gap-3">
                    <span className="overline text-white w-48">Logo height (px)</span>
                    {num("logo_height_px")}
                    <span className="text-streetz-muted text-xs">default 40</span>
                </label>
            </section>

            <hr className="border-streetz-line" />

            <section className="flex flex-col gap-4">
                <h3 className="overline text-streetz-yellow">FONT SIZES</h3>
                <p className="text-xs text-streetz-muted">Leave blank or 0 to keep the responsive defaults. Values are in pixels.</p>
                <label className="flex items-center gap-3 flex-wrap">
                    <span className="overline text-white w-48">Hero headline</span>
                    {num("font_hero_px")}
                    <span className="text-streetz-muted text-xs">default 80px desktop</span>
                </label>
                <label className="flex items-center gap-3 flex-wrap">
                    <span className="overline text-white w-48">Section headings</span>
                    {num("font_section_px")}
                    <span className="text-streetz-muted text-xs">default 48px desktop</span>
                </label>
                <label className="flex items-center gap-3 flex-wrap">
                    <span className="overline text-white w-48">Body text</span>
                    {num("font_body_px")}
                    <span className="text-streetz-muted text-xs">default 16px</span>
                </label>
                <label className="flex items-center gap-3 flex-wrap">
                    <span className="overline text-white w-48">Navigation links</span>
                    {num("font_nav_px")}
                    <span className="text-streetz-muted text-xs">default 11px</span>
                </label>
            </section>

            <div className="flex flex-wrap gap-3 pt-2 border-t border-streetz-line">
                <button disabled={saving} type="submit" data-testid="branding-save-button" className="bg-streetz-red text-white overline py-3 px-6 hover:bg-white hover:text-black disabled:opacity-60">
                    {saving ? "SAVING…" : "SAVE BRANDING"}
                </button>
                <button type="button" onClick={reset} data-testid="branding-reset-button" className="overline border border-streetz-line text-streetz-muted px-4 py-3 hover:border-streetz-red hover:text-white">
                    RESET TO DEFAULTS
                </button>
            </div>
        </form>
    );
}
